import { useEffect, useState } from 'react';
import useThemeColor from './useThemeColor';

/**
 * Custom hook to get the value based on the active theme.
 *
 * @param lightValue - The value to be used in light mode.
 * @param darkValue - The value to be used in dark mode.
 * @param highContrastValue - The value to be used in high contrast mode.
 * @returns The value corresponding to the active theme.
 */
const useColorModeValue = (
  lightValue: string,
  darkValue: string,
  highContrastValue: string
): string => {
  const [theme, setTheme] = useState<'light' | 'dark' | 'highContrast'>(
    'light'
  );

  useEffect(() => {
    const mediaQueryDark = window.matchMedia('(prefers-color-scheme: dark)');
    const mediaQueryHighContrast = window.matchMedia('(forced-colors: active)');

    const updateTheme = () => {
      if (mediaQueryHighContrast.matches) {
        setTheme('highContrast');
      } else if (mediaQueryDark.matches) {
        setTheme('dark');
      } else {
        setTheme('light');
      }
    };

    mediaQueryDark.addEventListener('change', updateTheme);
    mediaQueryHighContrast.addEventListener('change', updateTheme);

    // Initial check
    updateTheme();

    return () => {
      mediaQueryDark.removeEventListener('change', updateTheme);
      mediaQueryHighContrast.removeEventListener('change', updateTheme);
    };
  }, []);

  const lightColor = useThemeColor(lightValue);
  const darkColor = useThemeColor(darkValue);

  switch (theme) {
    case 'dark':
      return darkColor !== '' ? darkColor : darkValue;
    case 'highContrast':
      return highContrastValue;
    case 'light':
    default:
      return lightColor !== '' ? lightColor : lightValue;
  }
};

export default useColorModeValue;
